import React from 'react';
import Icon from '../../../components/AppIcon';

const ComparisonDashboardEnhanced = ({ enhancedComparison, scanDates, onViewPreviousScan }) => {
    if (!enhancedComparison) return null;

    const {
        skill_gaps_delta,
        roadmap_updates,
        interview_prep_delta,
        progress_summary
    } = enhancedComparison;

    return (
        <div className="space-y-6">
            {/* Progress Summary - Hero Section */}
            <div className="bg-gradient-to-br from-primary/5 to-blue-50 border-2 border-primary/20 rounded-xl p-6 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                    <Icon name="TrendingUp" size={32} className="text-primary" />
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Your Progress Summary</h2>
                        <p className="text-sm text-gray-600">
                            {scanDates ? `Comparing with scan from ${scanDates.daysBetween} days ago` : 'Comparing with previous scan'}
                        </p>
                    </div>
                </div>

                {/* Key Wins */}
                {progress_summary.key_wins.length > 0 && (
                    <div className="mb-4">
                        <h3 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
                            <Icon name="CheckCircle" size={16} />
                            Key Wins 🎉
                        </h3>
                        <ul className="space-y-1">
                            {progress_summary.key_wins.map((win, idx) => (
                                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                    <span className="text-green-600">✓</span>
                                    <span>{win}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Focus Areas */}
                {progress_summary.focus_areas.length > 0 && (
                    <div className="mb-4">
                        <h3 className="text-sm font-semibold text-orange-800 mb-2 flex items-center gap-2">
                            <Icon name="Target" size={16} />
                            Areas Needing Attention
                        </h3>
                        <ul className="space-y-1">
                            {progress_summary.focus_areas.map((area, idx) => (
                                <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                                    <span className="text-orange-600">⚠</span>
                                    <span>{area}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Next Steps */}
                {progress_summary.next_steps.length > 0 && (
                    <div>
                        <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                            <Icon name="ArrowRight" size={16} />
                            Recommended Next Steps
                        </h3>
                        <ol className="space-y-1 list-decimal list-inside">
                            {progress_summary.next_steps.map((step, idx) => (
                                <li key={idx} className="text-sm text-gray-700">{step}</li>
                            ))}
                        </ol>
                    </div>
                )}
            </div>

            {/* Skill Gaps Delta */}
            {skill_gaps_delta && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="Activity" size={24} />
                        Skill Gap Changes
                    </h2>

                    <div className="grid md:grid-cols-3 gap-4">
                        {/* Gaps Closed */}
                        {skill_gaps_delta.gaps_closed && skill_gaps_delta.gaps_closed.length > 0 && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                                        {skill_gaps_delta.gaps_closed.length}
                                    </div>
                                    <h3 className="font-semibold text-green-800">Gaps Closed ✅</h3>
                                </div>
                                <div className="space-y-3">
                                    {skill_gaps_delta.gaps_closed.map((item, idx) => (
                                        <div key={idx} className="bg-white rounded p-3 border border-green-200">
                                            <p className="font-medium text-green-900 text-sm">{item.skill}</p>
                                            <p className="text-xs text-gray-600 mt-1">{item.how_addressed}</p>
                                            <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${item.impact === 'high' ? 'bg-green-600 text-white' :
                                                item.impact === 'medium' ? 'bg-green-500 text-white' :
                                                    'bg-green-400 text-white'
                                                }`}>
                                                {item.impact} impact
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Persistent Gaps */}
                        {skill_gaps_delta.persistent_gaps && skill_gaps_delta.persistent_gaps.length > 0 && (
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                                        {skill_gaps_delta.persistent_gaps.length}
                                    </div>
                                    <h3 className="font-semibold text-orange-800">Still Needed ⚠️</h3>
                                </div>
                                <div className="space-y-3">
                                    {skill_gaps_delta.persistent_gaps.map((item, idx) => (
                                        <div key={idx} className="bg-white rounded p-3 border border-orange-200">
                                            <p className="font-medium text-orange-900 text-sm">{item.skill}</p>
                                            <p className="text-xs text-gray-600 mt-1 font-medium">Action: {item.action_needed}</p>
                                            <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${item.severity === 'critical' ? 'bg-red-600 text-white' :
                                                item.severity === 'important' ? 'bg-orange-500 text-white' :
                                                    'bg-orange-400 text-white'
                                                }`}>
                                                {item.severity}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* New Gaps */}
                        {skill_gaps_delta.new_gaps && skill_gaps_delta.new_gaps.length > 0 && (
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                                        {skill_gaps_delta.new_gaps.length}
                                    </div>
                                    <h3 className="font-semibold text-blue-800">Newly Detected 🆕</h3>
                                </div>
                                <div className="space-y-3">
                                    {skill_gaps_delta.new_gaps.map((item, idx) => (
                                        <div key={idx} className="bg-white rounded p-3 border border-blue-200">
                                            <p className="font-medium text-blue-900 text-sm">{item.skill}</p>
                                            <p className="text-xs text-gray-600 mt-1">{item.why_detected}</p>
                                            <span className={`inline-block mt-2 px-2 py-0.5 rounded text-xs font-medium ${item.priority === 'high' ? 'bg-blue-600 text-white' :
                                                item.priority === 'medium' ? 'bg-blue-500 text-white' :
                                                    'bg-blue-400 text-white'
                                                }`}>
                                                {item.priority} priority
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Roadmap Updates */}
            {roadmap_updates && (roadmap_updates.new_items?.length > 0 || roadmap_updates.still_critical?.length > 0) && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="Map" size={24} />
                        Smart Roadmap (What's Changed)
                    </h2>

                    {/* New Recommendations */}
                    {roadmap_updates.new_items && roadmap_updates.new_items.length > 0 && (
                        <div className="mb-6">
                            <h3 className="text-lg font-semibold text-blue-900 mb-3">🆕 New Recommendations</h3>
                            <div className="space-y-4">
                                {roadmap_updates.new_items.map((item, idx) => (
                                    <div key={idx} className="border border-blue-200 rounded-lg p-4 bg-blue-50">
                                        <div className="flex items-start justify-between mb-2">
                                            <h4 className="font-semibold text-blue-900">{item.skill}</h4>
                                            <span className="text-xs px-2 py-1 bg-blue-600 text-white rounded">{item.timeline}</span>
                                        </div>
                                        <p className="text-sm text-gray-700 mb-2">{item.why_now}</p>
                                        <p className="text-sm font-medium text-blue-800">
                                            Start here: {item.first_step}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Still Critical */}
                    {roadmap_updates.still_critical && roadmap_updates.still_critical.length > 0 && (
                        <div>
                            <h3 className="text-lg font-semibold text-orange-900 mb-3">⚠️ Still Critical</h3>
                            <div className="space-y-3">
                                {roadmap_updates.still_critical.map((item, idx) => (
                                    <div key={idx} className="border border-orange-200 rounded-lg p-4 bg-orange-50">
                                        <h4 className="font-semibold text-orange-900">{item.skill}</h4>
                                        <p className="text-sm text-gray-700 mt-1">{item.updated_reasoning}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Interview Prep Delta */}
            {interview_prep_delta && interview_prep_delta.new_questions && interview_prep_delta.new_questions.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Icon name="MessageCircle" size={24} />
                        New Interview Questions
                    </h2>
                    <p className="text-sm text-gray-600 mb-4">
                        Based on skills you've added, expect these questions:
                    </p>
                    <div className="space-y-4">
                        {interview_prep_delta.new_questions.map((item, idx) => (
                            <div key={idx} className="border border-primary/20 rounded-lg p-4 bg-primary/5">
                                <p className="font-medium text-gray-900 mb-2">Q{idx + 1}: {item.question}</p>
                                <p className="text-sm text-gray-600 mb-2">{item.context}</p>
                                <div className="bg-white rounded p-3 border border-primary/20">
                                    <p className="text-xs font-semibold text-primary mb-1">💡 Answer Tip:</p>
                                    <p className="text-sm text-gray-700">{item.tip}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* View Previous Scan Link */}
            {onViewPreviousScan && (
                <div className="text-center">
                    <button
                        onClick={onViewPreviousScan}
                        className="text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1"
                    >
                        <Icon name="ChevronLeft" size={16} />
                        View Previous Scan
                    </button>
                </div>
            )}
        </div>
    );
};

export default ComparisonDashboardEnhanced;
